import { asset } from "./portal-env";

// Country (and a few regional/other) flags you can fly your worm under. The SVGs are
// circular icons under /flags (ISO code named, e.g. /flags/se.svg). Country names come
// from the built-in Intl.DisplayNames, so there's no hand-maintained name table.

export const FLAG_CODES: string[] = ["ad","ae","af","ag","ai","al","am","ao","aq","ar","as","at","au","aw","ax","az","ba","bb","bd","be","bf","bg","bh","bi","bj","bl","bm","bn","bo","bq-bo","bq-sa","bq-se","br","bs","bt","bw","by","bz","ca-bc","ca","cc","cd","cf","cg","ch","ci","ck","cl","cm","cn","co","cr","cu","cv","cw","cx","cy","cz","de","dj","dk","dm","do","dz","easter_island","ec-w","ec","ee","eg","eh","er","es-ce","es-cn","es-ga","es-ib","es-ml","es-pv","es-variant","es","esperanto","et","european_union","fi","fj","fk","fm","fo","fr-h","fr","ga","gb-eng","gb-ork","gb-sct","gb-wls","gb","gd","ge-ab","ge","gf","gg","gh","gi","gl","gm","gn","gp","gq","gr","gt","gu","gw","gy","hausa","hk","hmong","hn","hr","ht","hu","id","ie","il","im","in","io","iq","ir","is","it-82","it-88","it","je","jm","jo","jp","kannada","ke","kg","kh","ki","km","kn","kp","kr","kurdistan","kw","ky","kz","la","lb","lc","li","lk","lr","ls","lt","lu","lv","ly","ma","malayali","maori","mc","md","me","mg","mh","mk","ml","mm","mn","mo","mp","mq","mr","ms","mt","mu","mv","mw","mx","my","mz","na","nato","ne","nf","ng","ni","nl","no","northern_cyprus","np","nr","nu","nz","om","pa","pe","pf","pg","ph","pk","pl","pn","pr","ps","pt-20","pt-30","pt","pw","py","qa","re","ro","rs","ru","rw","sa","sb","sc","sd","se","sg","sh","si","sindh","sk","sl","sm","sn","so","somaliland","south_ossetia","sr","ss","st","sv","sx","sy","sz","tc","td","tf","tg","th","tibet","tj","tk","tl","tm","tn","to","tr","transnistria","tt","tv","tw","tz","ua","ug","united_nations","us-hi","us","uy","uz","va","vc","ve","vg","vi","vn","vu","ws","xk","xx","ye","yiddish","yt","za","zm","zw"];

// Purchasable novelty flags (see core/src/shop/shop-catalog.js): valid codes
// with assets like any country flag. They ARE in FLAG_CODES so the flag picker
// can show them locked with their egg price until owned (a locked click leads
// to the shop). In the shop they render as the SQUARE cards (flagUrlSquare).
export const NOVELTY_FLAG_CODES: string[] = ["pirate", "checkered", "moon", "earth", "pizza", "duck", "shadow", "shuriken", "goodboy", "frog", "illuminati", "catnoir", "goldenegg"];
FLAG_CODES.push(...NOVELTY_FLAG_CODES);

// The animated novelty flags (CSS inside the SVG, so they move even via <img>).
// These sit in the epic/legendary shop tier, priced above the static ones.
export const ANIMATED_FLAG_CODES: string[] = ["pirate", "earth", "duck", "goldenegg", "illuminati", "shuriken"];

const CODE_SET = new Set(FLAG_CODES);
export function isFlagCode(code: string | null | undefined): boolean {
  return !!code && CODE_SET.has(code);
}

export function flagUrl(code: string): string {
  return asset(`/flags/${(isFlagCode(code) ? code : "xx")}.svg`);
}
// Square (unmasked) version of the same flag, for card / rectangular displays.
export function flagUrlSquare(code: string): string {
  return asset(`/flags-square/${(isFlagCode(code) ? code : "xx")}.svg`);
}

// Readable names for the non-ISO entries (regions, languages, orgs) so search works.
const SPECIAL: Record<string, string> = {
  xx: "No flag", european_union: "European Union", united_nations: "United Nations", nato: "NATO",
  esperanto: "Esperanto", easter_island: "Easter Island", kurdistan: "Kurdistan", tibet: "Tibet",
  somaliland: "Somaliland", northern_cyprus: "Northern Cyprus", south_ossetia: "South Ossetia",
  transnistria: "Transnistria", hausa: "Hausa", hmong: "Hmong", kannada: "Kannada",
  malayali: "Malayali", maori: "Maori", sindh: "Sindh", yiddish: "Yiddish",
  // Novelty (shop) flags
  pirate: "Jolly Roger", checkered: "Finish Line",
  moon: "Moon", earth: "Planet Earth", pizza: "Pizza Slice", duck: "Rubber Duck",
  shadow: "Shadow", shuriken: "Shuriken", goodboy: "Good Boy", frog: "Frog", illuminati: "Illuminati", catnoir: "Cat Noir", goldenegg: "The Golden Egg",
};

let regionNames: Intl.DisplayNames | null = null;
let regionNamesEn: Intl.DisplayNames | null = null;
try { regionNames = new Intl.DisplayNames(undefined, { type: "region" }); } catch { regionNames = null; }
try { regionNamesEn = new Intl.DisplayNames(["en"], { type: "region" }); } catch { regionNamesEn = null; }

function regionName(code: string, dn: Intl.DisplayNames | null): string {
  if (/^[a-z]{2}$/.test(code) && dn) {
    try { const n = dn.of(code.toUpperCase()); if (n && n.toLowerCase() !== code) return n; } catch { /* unknown region */ }
  }
  return "";
}

// Display name, in the viewer's own language (e.g. "Sverige" on a Swedish browser).
export function flagName(code: string): string {
  if (SPECIAL[code]) return SPECIAL[code];
  return regionName(code, regionNames) || code.replace(/[-_]/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

// Lowercase haystack for searching: the localized name, the English name, and the code,
// so "sweden", "sverige" and "se" all find Sweden regardless of browser language.
export function flagSearchText(code: string): string {
  return `${flagName(code)} ${regionName(code, regionNamesEn)} ${SPECIAL[code] || ""} ${code}`.toLowerCase();
}

// Best guess for a first-time player's flag: the region from the browser locale
// (e.g. sv-SE -> se). Falls back to the neutral "xx" flag when it can't tell.
export function defaultFlagCode(): string {
  try {
    const langs = (navigator.languages && navigator.languages.length) ? navigator.languages : [navigator.language];
    for (const l of langs) {
      const m = /[-_]([A-Za-z]{2})$/.exec(l || "");
      if (m) { const code = m[1].toLowerCase(); if (CODE_SET.has(code)) return code; }
    }
  } catch { /* no navigator (SSR) */ }
  return "xx";
}
